import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { recruiterAPI, profileAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { 
  Briefcase, LogOut, Plus, Users, Edit, Trash2, User, Search, Download,
  Loader2, UserPlus, UserMinus, Eye, MapPin, Clock, IndianRupee, Phone,
  Upload, FileText, Camera, Building2, Sparkles, ArrowUpDown, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import CandidateFilters from '../components/CandidateFilters';
import NotificationBell from '../components/NotificationBell';
import * as XLSX from 'xlsx';

// Helper function to check if candidate is new (joined within last 30 days)
const isNewCandidate = (createdAt) => {
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return createdDate >= thirtyDaysAgo;
};

const RecruiterDashboard = () => {
  const [companyHRList,setCompanyHRList]= useState([]); 
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [jobs, setJobs] = useState([]);
  const [allLiveJobs, setAllLiveJobs] = useState([]);
  const [myJobSeekers, setMyJobSeekers] = useState([]);
  const [allJobSeekers, setAllJobSeekers] = useState([]);
  const [activeTab, setActiveTab] = useState('myJobs');
  const [companies, setCompanies] = useState([]); // Companies list for dropdown
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  
  // Candidate filter states
  const [candidateFilters, setCandidateFilters] = useState({
    experience: [],
    salary: [],
    noticePeriod: [],
    location: '',
    profileStatus: [],
  });
  const [candidateSearchQuery, setCandidateSearchQuery] = useState('');
  const [filteredJobSeekersLoading, setFilteredJobSeekersLoading] = useState(false);
  const [candidateSortBy, setCandidateSortBy] = useState('newest'); // 'newest', 'oldest', 'name'
  
  // Selected candidates for export
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [downloadLimit, setDownloadLimit] = useState({ dailyLimit: 50, downloadedToday: 0, remaining: 50 });
  const [exportingExcel, setExportingExcel] = useState(false);
  
  // Modal states
  const [showJobModal, setShowJobModal] = useState(false);
  const [showJobSeekerModal, setShowJobSeekerModal] = useState(false);
  const [showAddToPipelineModal, setShowAddToPipelineModal] = useState(false);
  const [selectedJobForPipeline, setSelectedJobForPipeline] = useState(null);
  const [pipelineSearchQuery, setPipelineSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  
  // View/Edit candidate modal states
  const [showViewCandidateModal, setShowViewCandidateModal] = useState(false);
  const [showEditCandidateModal, setShowEditCandidateModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [loadingCandidateDetails, setLoadingCandidateDetails] = useState(false);
  const [showPipelineSelector, setShowPipelineSelector] = useState(false);
  const [selectedJobForCandidate, setSelectedJobForCandidate] = useState('');
  const [addingToPipeline, setAddingToPipeline] = useState(false);
  const [editCandidateForm, setEditCandidateForm] = useState({
    fullName: '',
    phone: '',
    location: '',
    resumeHeadline: '',
    experience: '',
    currentRole: '',
    currentCompany: '',
    currentPackage: '',
    expectedPackage: '',
    noticePeriod: '',
    industry: '',
    skills: '',
    linkedinUrl: '',
    portfolioUrl: '',
  });
  
  // File upload states for candidate
  const [candidateResumeFile, setCandidateResumeFile] = useState(null);
  const [candidatePhotoFile, setCandidatePhotoFile] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Job form data
  const [jobForm, setJobForm] = useState({
    title: '',
    company: '',
    experience: '',
    package: '',
    numberOfOpenings: 1,
    location: '',
    jobDescription: '',
    role: '',
    industryType: '',
    department: '',
    employmentType: 'Full Time',
    roleCategory: '',
    keySkills: '',
    educationLevel: '',
    course: '',
    fieldOfStudy: '',
  });
  
  // Job seeker form - comprehensive
  const [jobSeekerForm, setJobSeekerForm] = useState({
    // Account Details
    email: '',
    password: '',
    fullName: '',
    mobile: '',
    
    // Professional Summary
    resumeHeadline: '',
    totalExperience: '',
    currentCompany: '',
    currentDesignation: '',
    currentLocation: '',
    currentSalary: '',
    expectedSalary: '',
    noticePeriod: '',
    
    // Skills
    skills: '',
    
    // Education
    highestQualification: '',
    specialization: '',
    university: '',
    graduationYear: '',
    
    // Previous Employment
    previousCompany: '',
    previousDesignation: '',
    previousDuration: '',
    
    // Additional
    industry: '',
    functionalArea: '',
    linkedinUrl: '',
    portfolioUrl: '',
  });

  const resetJobSeekerForm = () => {
    setJobSeekerForm({
      email: '',
      password: '',
      fullName: '',
      mobile: '',
      resumeHeadline: '',
      totalExperience: '',
      currentCompany: '',
      currentDesignation: '',
      currentLocation: '',
      currentSalary: '',
      expectedSalary: '',
      noticePeriod: '',
      skills: '',
      highestQualification: '',
      specialization: '',
      university: '',
      graduationYear: '',
      previousCompany: '',
      previousDesignation: '',
      previousDuration: '',
      industry: '',
      functionalArea: '',
      linkedinUrl: '',
      portfolioUrl: '',
    });
  };

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    if (role !== 'recruiter' && role !== 'super_admin') {
      toast({
        title: 'Access Denied',
        description: 'You must be a Recruiter to access this page',
        variant: 'destructive',
      });
      navigate('/recruiter/login');
      return;
    }
    
    fetchDashboardData();
  }, [navigate]);

  // Fetch filtered job seekers when filters change
  const fetchFilteredJobSeekers = useCallback(async () => {
    try {
      setFilteredJobSeekersLoading(true);
      const filters = {
        ...candidateFilters,
        search: candidateSearchQuery,
      };
      const allSeekersRes = await recruiterAPI.getAllJobSeekers(filters);
      setAllJobSeekers(Array.isArray(allSeekersRes?.data) ? allSeekersRes.data : allSeekersRes || []);
    } catch (error) {
      console.error('Error fetching filtered job seekers:', error);
      toast({
        title: 'Error',
        description: 'Failed to filter job seekers',
        variant: 'destructive',
      });
    } finally {
      setFilteredJobSeekersLoading(false);
    }
  }, [candidateFilters, candidateSearchQuery]);

  // Debounce filter changes
  useEffect(() => {
    if (activeTab === 'allJobSeekers') {
      const debounceTimer = setTimeout(() => {
        fetchFilteredJobSeekers();
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [candidateFilters, candidateSearchQuery, activeTab, fetchFilteredJobSeekers]);

  // Sort candidates based on selected sort option
  const sortedAllJobSeekers = React.useMemo(() => {
    const sorted = [...allJobSeekers];
    switch (candidateSortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      case 'name':
        return sorted.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
      default:
        return sorted;
    }
  }, [allJobSeekers, candidateSortBy]);

  // Pagination for All Job Seekers
  const totalPages = Math.ceil(sortedAllJobSeekers.length / itemsPerPage);
  const paginatedJobSeekers = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedAllJobSeekers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedAllJobSeekers, currentPage, itemsPerPage]);

  // Sort myJobSeekers by newest first
  const sortedMyJobSeekers = React.useMemo(() => {
    return [...myJobSeekers].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [myJobSeekers]);

  // Filter candidates for pipeline modal search
  const filteredPipelineCandidates = React.useMemo(() => {
    if (!pipelineSearchQuery.trim()) return allJobSeekers;
    const query = pipelineSearchQuery.toLowerCase().trim();
    return allJobSeekers.filter(seeker => 
      (seeker.fullName?.toLowerCase().includes(query)) ||
      (seeker.email?.toLowerCase().includes(query)) ||
      (seeker.skills?.some(skill => skill.toLowerCase().includes(query))) ||
      (seeker.headline?.toLowerCase().includes(query))
    );
  }, [allJobSeekers, pipelineSearchQuery]);

  const handleClearCandidateFilters = () => {
    setCandidateFilters({
      experience: [],
      salary: [],
      noticePeriod: [],
      location: '',
      profileStatus: [],
    });
    setCandidateSearchQuery('');
    setCurrentPage(1);
  };

  // Candidate selection handlers
  const handleSelectCandidate = (candidateId, isSelected) => {
    if (isSelected) {
      setSelectedCandidates(prev => [...prev, candidateId]);
    } else {
      setSelectedCandidates(prev => prev.filter(id => id !== candidateId));
    }
  };

  const handleSelectAllOnPage = (isSelected) => {
    if (isSelected) {
      const pageIds = paginatedJobSeekers.map(s => s.id);
      setSelectedCandidates(prev => [...new Set([...prev, ...pageIds])]);
    } else {
      const pageIds = paginatedJobSeekers.map(s => s.id);
      setSelectedCandidates(prev => prev.filter(id => !pageIds.includes(id)));
    }
  };

  const isAllOnPageSelected = paginatedJobSeekers.length > 0 && 
    paginatedJobSeekers.every(s => selectedCandidates.includes(s.id));

// ===============================
// FETCH DOWNLOAD LIMIT
// ===============================
const fetchDownloadLimit = useCallback(async () => {
  try {

    const response = await recruiterAPI.getDownloadLimit();

    const res = response?.data || response;

    if (res?.success) {
      setDownloadLimit({
        dailyLimit: res.dailyLimit || 50,
        downloadedToday: res.downloadedToday || 0,
        remaining: res.remaining || 0
      });
    }

  } catch (error) {
    console.error("Error fetching download limit:", error);
  }
}, []);


// ===============================
// LOAD LIMIT ON PAGE OPEN
// ===============================
useEffect(() => {
  fetchDownloadLimit();
}, []);


// ===============================
// LOAD LIMIT WHEN TAB CHANGES
// ===============================
useEffect(() => {
  if (activeTab === "allJobSeekers") {
    fetchDownloadLimit();
  }
}, [activeTab, fetchDownloadLimit]);


// ===============================
// DOWNLOAD RESUME
// ===============================
const downloadResume = async (candidate) => {

  try {

    const response = await recruiterAPI.logDownload({
      candidateIds: [candidate.id],
      candidateNames: [candidate.fullName],
      downloadFormat: "resume"
    });

    const res = response?.data || response;

    if (res?.success) {

      setDownloadLimit(prev => ({
        ...prev,
        downloadedToday: res.downloadedToday,
        remaining: res.remaining
      }));

    }

    // open resume
    window.open(candidate.resumeUrl, "_blank");

  } catch (error) {

    console.error("Resume download error:", error);

    toast({
      title: "Download failed",
      description: "Unable to download resume",
      variant: "destructive"
    });

  }

};

const exportToExcel = async (format) => {

  if (selectedCandidates.length === 0) {
    toast({
      title: "No candidates selected",
      description: "Please select at least one candidate to export",
      variant: "destructive",
    });
    return;
  }

  if (selectedCandidates.length > downloadLimit.remaining) {
    toast({
      title: "Download limit exceeded",
      description: `You can only download ${downloadLimit.remaining} more candidate(s) today.`,
      variant: "destructive",
    });
    return;
  }

  setExportingExcel(true);

  try {

    const candidatesToExport = allJobSeekers.filter(s =>
      selectedCandidates.includes(s.id)
    );

    const candidateIds = candidatesToExport.map(c => c.id);
    const candidateNames = candidatesToExport.map(c => c.fullName || c.email);

    // Log download
    const logResponse = await recruiterAPI.logDownload({
      candidateIds,
      candidateNames,
      downloadFormat: format
    });

    const res = logResponse?.data || logResponse;

    if (!res?.success) {
      throw new Error(res?.detail || "Failed to log download");
    }

    setDownloadLimit(prev => ({
      ...prev,
      downloadedToday: res.downloadedToday,
      remaining: res.remaining
    }));


    let data;
    let headers;

    // ==========================
    // BASIC FORMAT
    // ==========================
    if (format === "basic") {

      headers = [
        "Candidate Name",
        "Current Company",
        "Designation",
        "Industry",
        "Experience",
        "Current CTC",
        "Expected CTC",
        "Notice Period",
        "Location"
      ];

      data = candidatesToExport.map(c => ({
        "Candidate Name": c.fullName || "",
        "Current Company": c.currentCompany || "",
        "Designation": c.currentRole || "",
        "Industry": c.industry || "",
        "Experience": c.experience || "",
        "Current CTC": c.currentPackage || "",
        "Expected CTC": c.expectedPackage || "",
        "Notice Period": c.noticePeriod || "",
        "Location": c.location || ""
      }));

    }

    // ==========================
// FULL FORMAT (WITH CONTACT)
// ==========================
else {

  headers = [
    'Name',
    'Mobile Number',
    'Email ID',
    'Current Company',
    'Designation',
    'Education',
    'Experience',
    'CTC',
    'Expected CTC',
    'Notice Period',
    'Location'
  ];

  data = candidatesToExport.map(c => ({

    'Name': c.fullName || '',

    'Mobile Number':
      c.phone ||
      c.mobile ||
      c.mobileNumber ||
      c.contactNumber ||
      c.profile?.phone ||
      c.user?.phone ||
      '',

    'Email ID': c.email || '',

    'Current Company':
      c.currentCompany ||
      c.currentEmployer ||
      '',

    'Designation':
      c.currentRole ||
      c.currentDesignation ||
      '',

    'Education':
      c.education ||
      c.highestQualification ||
      '',

    'Experience': c.experience || '',

    'CTC':
      c.currentPackage ||
      c.currentSalary ||
      '',

    'Expected CTC':
      c.expectedPackage ||
      c.expectedSalary ||
      '',

    'Notice Period': c.noticePeriod || '',

    'Location': c.location || ''
  }));
}
    const ws = XLSX.utils.json_to_sheet(data, { header: headers });

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Candidates");


    ws["!cols"] = headers.map(h => ({
      wch: Math.max(h.length + 2, 18)
    }));


    const date = new Date().toISOString().split("T")[0];

    const filename =
      format === "basic"
        ? `jobgenie_candidates_basic_${date}.xlsx`
        : `jobgenie_candidates_full_${date}.xlsx`;


    XLSX.writeFile(wb, filename);


    setSelectedCandidates([]);


    toast({
      title: "Export successful",
      description: `${candidatesToExport.length} candidate(s) exported. Remaining today: ${res.remaining}`,
    });

  } catch (error) {

    console.error("Export error:", error);

    const errorMessage =
      error.response?.data?.detail ||
      error.message ||
      "Failed to export candidates";

    toast({
      title: "Export failed",
      description: errorMessage,
      variant: "destructive",
    });

    fetchDownloadLimit();

  } finally {
    setExportingExcel(false);
  }
};

  // View candidate details
  const handleViewCandidate = async (seeker) => {
    try {
      setSelectedCandidate(seeker);
      setShowViewCandidateModal(true);
      setLoadingCandidateDetails(true);
      
      const response = await recruiterAPI.getJobSeekerDetails(seeker.id);
      setCandidateDetails(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load candidate details',
        variant: 'destructive',
      });
    } finally {
      setLoadingCandidateDetails(false);
    }
  };

  // Edit candidate - open modal with form data
  const handleEditCandidate = async (seeker) => {
    try {
      setSelectedCandidate(seeker);
      setLoadingCandidateDetails(true);
      setCandidateResumeFile(null);
      setCandidatePhotoFile(null);
      
      const response = await recruiterAPI.getJobSeekerDetails(seeker.id);
      const profile = response.data.profile || {};
      
      setEditCandidateForm({
        fullName: response.data.fullName || '',
        phone: profile.phone || response.data.mobile || '',
        location: profile.location || '',
        resumeHeadline: profile.headline || '',
        experience: profile.experience || '',
        currentRole: profile.currentRole || '',
        currentCompany: profile.currentCompany || '',
        currentPackage: profile.currentSalary || '',
        expectedPackage: profile.expectedSalary || '',
        noticePeriod: profile.noticePeriod || '',
        industry: profile.industry || '',
        skills: Array.isArray(profile.skills) ? profile.skills.join(', ') : '',
        linkedinUrl: profile.linkedinUrl || '',
        portfolioUrl: profile.portfolioUrl || '',
      });
      
      // Store candidate email for file uploads
      setCandidateDetails(response.data);
      setShowEditCandidateModal(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load candidate details for editing',
        variant: 'destructive',
      });
    } finally {
      setLoadingCandidateDetails(false);
    }
  };

  // Save edited candidate
  const handleSaveCandidate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      
      const updateData = {
        ...editCandidateForm,
        skills: editCandidateForm.skills ? editCandidateForm.skills.split(',').map(s => s.trim()).filter(s => s) : [],
      };
      
      await recruiterAPI.updateJobSeeker(selectedCandidate.id, updateData);
      
      // Upload resume if selected
      if (candidateResumeFile && candidateDetails?.email) {
        try {
          setUploadingResume(true);
          await profileAPI.uploadResume(candidateDetails.email, candidateResumeFile);
          toast({ title: 'Success', description: 'Resume uploaded successfully' });
        } catch (err) {
          toast({ title: 'Warning', description: 'Profile saved but resume upload failed', variant: 'destructive' });
        } finally {
          setUploadingResume(false);
        }
      }
      
      // Upload photo if selected
      if (candidatePhotoFile && candidateDetails?.email) {
        try {
          setUploadingPhoto(true);
          await profileAPI.uploadPhoto(candidateDetails.email, candidatePhotoFile);
          toast({ title: 'Success', description: 'Photo uploaded successfully' });
        } catch (err) {
          toast({ title: 'Warning', description: 'Profile saved but photo upload failed', variant: 'destructive' });
        } finally {
          setUploadingPhoto(false);
        }
      }
      
      toast({
        title: 'Success',
        description: 'Candidate profile updated successfully',
      });
      
      setCandidateResumeFile(null);
      setCandidatePhotoFile(null);
      setShowEditCandidateModal(false);
      fetchFilteredJobSeekers(); // Refresh the list
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update candidate',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle resume file selection
  const handleResumeFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['.pdf', '.doc', '.docx'];
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!allowedTypes.includes(ext)) {
        toast({ title: 'Error', description: 'Only PDF, DOC, DOCX files allowed', variant: 'destructive' });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: 'Error', description: 'File size must be less than 5MB', variant: 'destructive' });
        return;
      }
      setCandidateResumeFile(file);
    }
  };
  
  // Handle photo file selection
  const handlePhotoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({ title: 'Error', description: 'Only JPEG, PNG, GIF, WebP images allowed', variant: 'destructive' });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast({ title: 'Error', description: 'Image size must be less than 2MB', variant: 'destructive' });
        return;
      }
      setCandidatePhotoFile(file);
    }
  };

  // ===============================
// Add candidate to pipeline
// ===============================
const handleAddCandidateToPipeline = async () => {
  if (!selectedJobForCandidate || !selectedCandidate) {
    toast({
      title: 'Error',
      description: 'Please select a job',
      variant: 'destructive',
    });
    return;
  }

  try {
    setAddingToPipeline(true);

    await recruiterAPI.addToPipeline(
      selectedJobForCandidate,
      selectedCandidate.id
    );

    const selectedJob = allLiveJobs.find(
      (j) => j.id === selectedJobForCandidate
    );

    toast({
      title: 'Success',
      description: `${
        selectedCandidate.fullName || candidateDetails?.fullName
      } added to ${selectedJob?.title || 'job'} pipeline`,
    });

    setShowPipelineSelector(false);
    setSelectedJobForCandidate('');

    if (selectedCandidate) {
      const response = await recruiterAPI.getJobSeekerDetails(
        selectedCandidate.id
      );
      setCandidateDetails(response.data);
    }
  } catch (error) {
    toast({
      title: 'Error',
      description:
        error.response?.data?.detail ||
        'Failed to add candidate to pipeline',
      variant: 'destructive',
    });
  } finally {
    setAddingToPipeline(false);
  }
};

// ===============================
// Assign candidate to HR  🔥 NEW
// ===============================
const handleAssignToHR = async (applicationId, candidateName) => {
  try {
    console.log("Assign Clicked:",applicationId)
    const response= await
    recruiterAPI.assignToHR(applicationId);
    toast({
      title: "Success",
      description:`${candidateName} assigned to HR Successfully`,
    });

    //Optional Refresh 
    if (selectedCandidate){
      const res = await
      recruiterAPI.getJobSeekerDetails(
        selectedCandidate.id
      );
      setCandidateDetails(res.data);
    }
  } catch (error) {
    toast({
      title: 'Error',
      description:
        error.response?.data?.detail ||
        'Failed to assign candidate to HR',
      variant: 'destructive',
    });
  }
};

// ===============================
// Withdraw candidate from HR
// ===============================
const handleWithdrawFromHR = async (
  applicationId,
  candidateName
) => {
  if (
    !window.confirm(
      `Withdraw ${candidateName} from Company HR?`
    )
  )
    return;

  try {
    await recruiterAPI.withdrawCandidate(applicationId);

    toast({
      title: 'Success',
      description: `${candidateName} withdrawn from HR pipeline`,
    });

    if (selectedCandidate) {
      const response = await recruiterAPI.getJobSeekerDetails(
        selectedCandidate.id
      );
      setCandidateDetails(response.data);
    }
  } catch (error) {
    toast({
      title: 'Error',
      description:
        error.response?.data?.detail ||
        'Failed to withdraw candidate',
      variant: 'destructive',
    });
  }
};

// ===============================
// Reassign candidate to HR
// ===============================
const handleReassignToHR = async (
  applicationId,
  candidateName
) => {
  try {
    await recruiterAPI.reassignCandidate(applicationId);

    toast({
      title: 'Success',
      description: `${candidateName} re-assigned to HR pipeline`,
    });

    if (selectedCandidate) {
      const response = await recruiterAPI.getJobSeekerDetails(
        selectedCandidate.id
      );
      setCandidateDetails(response.data);
    }
  } catch (error) {
    toast({
      title: 'Error',
      description:
        error.response?.data?.detail ||
        'Failed to re-assign candidate',
      variant: 'destructive',
    });
  }
};

const fetchDashboardData = async () => {
  try {
    setLoading(true);

    const results = await Promise.allSettled([
      recruiterAPI.getStats(),
      recruiterAPI.getMyJobs(),
      recruiterAPI.getMyJobSeekers(),
      recruiterAPI.getAllLiveJobs(),
      recruiterAPI.getAllJobSeekers(),
      recruiterAPI.getCompanies(),
    ]);

    const getValue = (res) =>
      res.status === "fulfilled" ? res.value : null;

    const statsData = getValue(results[0]);
    const jobsData = getValue(results[1]);
    const mySeekersData = getValue(results[2]);
    const allLiveJobsData = getValue(results[3]);
    const allSeekersData = getValue(results[4]);
    const companiesData = getValue(results[5]);

    // Stats
    setStats(statsData?.data || statsData || {});

    // My Jobs
    setJobs(
      Array.isArray(jobsData?.data)
        ? jobsData.data
        : Array.isArray(jobsData)
        ? jobsData
        : []
    );

    // My Job Seekers
    setMyJobSeekers(
      Array.isArray(mySeekersData?.data)
        ? mySeekersData.data
        : Array.isArray(mySeekersData)
        ? mySeekersData
        : []
    );

    // All Live Jobs
    setAllLiveJobs(
      Array.isArray(allLiveJobsData?.data)
        ? allLiveJobsData.data
        : Array.isArray(allLiveJobsData)
        ? allLiveJobsData
        : []
    );

    // All Job Seekers
    setAllJobSeekers(
      Array.isArray(allSeekersData?.data)
        ? allSeekersData.data
        : Array.isArray(allSeekersData)
        ? allSeekersData
        : []
    );

    // Companies
    setCompanies(
      Array.isArray(companiesData?.data)
        ? companiesData.data
        : Array.isArray(companiesData)
        ? companiesData
        : []
    );

  } catch (error) {
    console.error("Dashboard fatal error:", error);

    toast({
      title: "Error",
      description: "Failed to load dashboard data",
      variant: "destructive",
    });
  } finally {
    setLoading(false);
  }
};
  
const handleLogout = () => {
    localStorage.clear();
    navigate('/recruiter/login');
  };

  const resetJobForm = () => {
    setJobForm({
      title: '',
      company: '',
      experience: '',
      package: '',
      numberOfOpenings: 1,
      location: '',
      jobDescription: '',
      role: '',
      industryType: '',
      department: '',
      employmentType: 'Full Time',
      roleCategory: '',
      keySkills: '',
      educationLevel: '',
      course: '',
      fieldOfStudy: '',
    });
    setEditingJob(null);
  };

  const handlePostJob = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      
      const jobData = {
        ...jobForm,
        keySkills: jobForm.keySkills.split(',').map(s => s.trim()).filter(Boolean),
        numberOfOpenings: parseInt(jobForm.numberOfOpenings) || 1,
      };

      if (editingJob) {
        await recruiterAPI.updateJob(editingJob.id, jobData);
        toast({ title: 'Success', description: 'Job updated successfully' });
      } else {
        await recruiterAPI.postJob(jobData);
        toast({ title: 'Success', description: 'Job posted successfully' });
      }
      
      setShowJobModal(false);
      resetJobForm();
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save job',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditJob = (job) => {
    setJobForm({
      title: job.title || '',
      company: job.company || '',
      experience: job.experience || '',
      package: job.package || '',
      numberOfOpenings: job.numberOfOpenings || 1,
      location: job.location || '',
      jobDescription: job.jobDescription || '',
      role: job.role || '',
      industryType: job.industryType || '',
      department: job.department || '',
      employmentType: job.employmentType || 'Full Time',
      roleCategory: job.roleCategory || '',
      keySkills: Array.isArray(job.keySkills) ? job.keySkills.join(', ') : '',
      educationLevel: job.educationLevel || '',
      course: job.course || '',
      fieldOfStudy: job.fieldOfStudy || '',
    });
    setEditingJob(job);
    setShowJobModal(true);
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    
    try {
      await recruiterAPI.deleteJob(jobId);
      toast({ title: 'Success', description: 'Job deleted successfully' });
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete job',
        variant: 'destructive',
      });
    }
  };

  const handleOpenAddToPipeline = (job) => {
    setSelectedJobForPipeline(job);
    setShowAddToPipelineModal(true);
  };

  const handleAddToPipeline = async (seekerId) => {
    if (!selectedJobForPipeline) return;
    
    try {
      setSubmitting(true);
      await recruiterAPI.addToPipeline(selectedJobForPipeline.id, seekerId);
      toast({ 
        title: 'Success', 
        description: `Candidate added to ${selectedJobForPipeline.title} pipeline` 
      });
      setShowAddToPipelineModal(false);
      setSelectedJobForPipeline(null);
      fetchDashboardData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add candidate to pipeline',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddJobSeeker = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      
      // Prepare data with skills as array
      const submitData = {
        ...jobSeekerForm,
        skills: jobSeekerForm.skills ? jobSeekerForm.skills.split(',').map(s => s.trim()).filter(s => s) : [],
      };
      
      await recruiterAPI.addJobSeeker(submitData);
      toast({ title: 'Success', description: 'Job seeker added successfully with full profile' });
      setShowJobSeekerModal(false);
      resetJobSeekerForm();
      
      // Refresh the job seekers list
      const seekersRes = await recruiterAPI.getMyJobSeekers();
      setMyJobSeekers(Array.isArray(seekersRes?.data) ? seekersRes.data : seekersRes || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add job seeker',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-gray-900" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Recruiter Dashboard</h1>
              <p className="text-sm text-gray-600">{localStorage.getItem('userEmail')}</p>
            </div>
          </div>
          
          {/* Navigation Links */}
          <nav className="hidden md:flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setActiveTab('myJobs')}
              className={`text-gray-600 hover:text-blue-600 hover:bg-blue-50 ${activeTab === 'myJobs' ? 'bg-blue-50 text-blue-600' : ''}`}
            >
              My Jobs
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setActiveTab('allJobs')}
              className={`text-gray-600 hover:text-blue-600 hover:bg-blue-50 ${activeTab === 'allJobs' ? 'bg-blue-50 text-blue-600' : ''}`}
            >
              All Live Jobs
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setActiveTab('myJobSeekers')}
              className={`text-gray-600 hover:text-blue-600 hover:bg-blue-50 ${activeTab === 'myJobSeekers' ? 'bg-blue-50 text-blue-600' : ''}`}
            >
              My Job Seekers
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setActiveTab('allJobSeekers')}
              className={`text-gray-600 hover:text-blue-600 hover:bg-blue-50 ${activeTab === 'allJobSeekers' ? 'bg-blue-50 text-blue-600' : ''}`}
            >
              All Job Seekers
            </Button>
          </nav>
          
          {/* Right side - Bell & Logout */}
          <div className="flex items-center gap-2">
            <NotificationBell userRole="recruiter" />
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              data-testid="recruiter-logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-white p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Jobs Posted</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="stat-jobs-posted">{stats.totalJobsPosted || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-white p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/recruiter/applicants')}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Applicants</p>
                <p className="text-3xl font-bold text-gray-900" data-testid="stat-applicants">{stats.totalApplicants || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Button 
            onClick={() => navigate('/recruiter/applicants')}
            className="bg-blue-600 hover:bg-blue-700 text-gray-900 shadow-sm"
            data-testid="view-applicants-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            View All Applicants
          </Button>
          <Dialog open={showJobModal} onOpenChange={(open) => {
            setShowJobModal(open);
            if (!open) resetJobForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-gray-900 shadow-sm" data-testid="post-job-btn">
                <Plus className="w-4 h-4 mr-2" />
                Post New Job
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
              <DialogHeader>
                <DialogTitle className="text-gray-900 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  {editingJob ? 'Edit Job Posting' : 'Post New Job'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handlePostJob} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Job Title *</Label>
                    <Input
                      required
                      value={jobForm.title}
                      onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., Senior Software Engineer"
                      data-testid="job-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Company Name *</Label>
                    <select
                      required
                      value={jobForm.company}
                      onChange={(e) => setJobForm({...jobForm, company: e.target.value})}
                      className="w-full h-10 px-3 bg-gray-50 border border-gray-300 text-gray-900 rounded-md focus:border-blue-500 focus:ring-blue-500 focus:outline-none"
                      data-testid="job-company-input"
                    >
                      <option value="">Select Company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.companyName}>
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                    {companies.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No companies found. Ask admin to add companies.</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Experience *</Label>
                    <Input
                      required
                      value={jobForm.experience}
                      onChange={(e) => setJobForm({...jobForm, experience: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., 3-5 years"
                      data-testid="job-experience-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Package/Salary *</Label>
                    <Input
                      required
                      value={jobForm.package}
                      onChange={(e) => setJobForm({...jobForm, package: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., 10-15 LPA"
                      data-testid="job-package-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Openings</Label>
                    <Input
                      type="number"
                      min="1"
                      value={jobForm.numberOfOpenings}
                      onChange={(e) => setJobForm({...jobForm, numberOfOpenings: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Location *</Label>
                    <Input
                      required
                      value={jobForm.location}
                      onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., Bangalore, Mumbai"
                      data-testid="job-location-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Employment Type</Label>
                    <select
                      value={jobForm.employmentType}
                      onChange={(e) => setJobForm({...jobForm, employmentType: e.target.value})}
                      className="w-full h-10 px-3 bg-gray-50 border border-gray-300 rounded-md text-gray-900"
                    >
                      <option value="Full Time">Full Time</option>
                      <option value="Part Time">Part Time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Role *</Label>
                    <Input
                      required
                      value={jobForm.role}
                      onChange={(e) => setJobForm({...jobForm, role: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., Backend Developer"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Industry Type *</Label>
                    <Input
                      required
                      value={jobForm.industryType}
                      onChange={(e) => setJobForm({...jobForm, industryType: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., IT Services"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Department</Label>
                    <Input
                      value={jobForm.department}
                      onChange={(e) => setJobForm({...jobForm, department: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., Engineering"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Role Category</Label>
                    <Input
                      value={jobForm.roleCategory}
                      onChange={(e) => setJobForm({...jobForm, roleCategory: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., Software Development"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-700 font-medium">Key Skills * (comma-separated)</Label>
                  <Input
                    required
                    value={jobForm.keySkills}
                    onChange={(e) => setJobForm({...jobForm, keySkills: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g., React, Node.js, MongoDB, AWS"
                    data-testid="job-skills-input"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-700 font-medium">Job Description *</Label>
                  <Textarea
                    required
                    rows={4}
                    value={jobForm.jobDescription}
                    onChange={(e) => setJobForm({...jobForm, jobDescription: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Describe the role, responsibilities, and requirements..."
                    data-testid="job-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Education Level</Label>
                    <Input
                      value={jobForm.educationLevel}
                      onChange={(e) => setJobForm({...jobForm, educationLevel: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., Bachelor's"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Course</Label>
                    <Input
                      value={jobForm.course}
                      onChange={(e) => setJobForm({...jobForm, course: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., B.Tech"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Field of Study</Label>
                    <Input
                      value={jobForm.fieldOfStudy}
                      onChange={(e) => setJobForm({...jobForm, fieldOfStudy: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="e.g., Computer Science"
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-job-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingJob ? 'Update Job' : 'Post Job'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showJobSeekerModal} onOpenChange={(open) => {
            setShowJobSeekerModal(open);
            if (!open) resetJobSeekerForm();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-cyan-500 text-blue-600 hover:bg-cyan-100" data-testid="add-seeker-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Job Seeker
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Add Job Seeker with Full Profile
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddJobSeeker} className="space-y-6">
                {/* Account Details Section */}
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-blue-600 font-medium mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Account Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Full Name *</Label>
                      <Input
                        required
                        value={jobSeekerForm.fullName}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, fullName: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="John Doe"
                        data-testid="seeker-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Mobile Number</Label>
                      <Input
                        value={jobSeekerForm.mobile}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, mobile: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Email *</Label>
                      <Input
                        type="email"
                        required
                        value={jobSeekerForm.email}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, email: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="email@example.com"
                        data-testid="seeker-email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Password *</Label>
                      <Input
                        type="password"
                        required
                        value={jobSeekerForm.password}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, password: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Create a password"
                        data-testid="seeker-password-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Summary Section */}
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-blue-600 font-medium mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Professional Summary
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Resume Headline</Label>
                      <Input
                        value={jobSeekerForm.resumeHeadline}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, resumeHeadline: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Senior Software Engineer | 5+ Years in Full Stack Development"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-gray-700 font-medium">Total Experience</Label>
                        <select
                          value={jobSeekerForm.totalExperience}
                          onChange={(e) => setJobSeekerForm({...jobSeekerForm, totalExperience: e.target.value})}
                          className="w-full h-10 px-3 bg-gray-50 border border-gray-300 text-gray-900 rounded-md focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select</option>
                          <option value="Fresher">Fresher</option>
                          <option value="0-1 years">0-1 years</option>
                          <option value="1-2 years">1-2 years</option>
                          <option value="2-3 years">2-3 years</option>
                          <option value="3-5 years">3-5 years</option>
                          <option value="5-7 years">5-7 years</option>
                          <option value="7-10 years">7-10 years</option>
                          <option value="10-15 years">10-15 years</option>
                          <option value="15+ years">15+ years</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-gray-700 font-medium">Notice Period</Label>
                        <select
                          value={jobSeekerForm.noticePeriod}
                          onChange={(e) => setJobSeekerForm({...jobSeekerForm, noticePeriod: e.target.value})}
                          className="w-full h-10 px-3 bg-gray-50 border border-gray-300 text-gray-900 rounded-md focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select</option>
                          <option value="Immediate">Immediate</option>
                          <option value="15 days">15 days</option>
                          <option value="30 days">30 days</option>
                          <option value="45 days">45 days</option>
                          <option value="60 days">60 days</option>
                          <option value="90 days">90 days</option>
                          <option value="More than 90 days">More than 90 days</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-gray-700 font-medium">Current Location</Label>
                        <Input
                          value={jobSeekerForm.currentLocation}
                          onChange={(e) => setJobSeekerForm({...jobSeekerForm, currentLocation: e.target.value})}
                          className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Bangalore"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-700 font-medium">Current Company</Label>
                        <Input
                          value={jobSeekerForm.currentCompany}
                          onChange={(e) => setJobSeekerForm({...jobSeekerForm, currentCompany: e.target.value})}
                          className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Company name"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700 font-medium">Current Designation</Label>
                        <Input
                          value={jobSeekerForm.currentDesignation}
                          onChange={(e) => setJobSeekerForm({...jobSeekerForm, currentDesignation: e.target.value})}
                          className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Software Engineer"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-700 font-medium">Current Salary (Annual)</Label>
                        <Input
                          value={jobSeekerForm.currentSalary}
                          onChange={(e) => setJobSeekerForm({...jobSeekerForm, currentSalary: e.target.value})}
                          className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="e.g., 12 LPA"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700 font-medium">Expected Salary (Annual)</Label>
                        <Input
                          value={jobSeekerForm.expectedSalary}
                          onChange={(e) => setJobSeekerForm({...jobSeekerForm, expectedSalary: e.target.value})}
                          className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="e.g., 18-20 LPA"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skills Section */}
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-blue-600 font-medium mb-3">Key Skills</h3>
                  <div>
                    <Label className="text-gray-700 font-medium">Skills (comma-separated)</Label>
                    <Input
                      value={jobSeekerForm.skills}
                      onChange={(e) => setJobSeekerForm({...jobSeekerForm, skills: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="React, Node.js, Python, AWS, MongoDB"
                    />
                  </div>
                </div>

                {/* Education Section */}
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-blue-600 font-medium mb-3">Education</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Highest Qualification</Label>
                      <select
                        value={jobSeekerForm.highestQualification}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, highestQualification: e.target.value})}
                        className="w-full h-10 px-3 bg-gray-50 border border-gray-300 text-gray-900 rounded-md focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="">Select</option>
                        <option value="10th">10th</option>
                        <option value="12th">12th</option>
                        <option value="Diploma">Diploma</option>
                        <option value="Bachelors">Bachelors Degree</option>
                        <option value="Masters">Masters Degree</option>
                        <option value="PhD">PhD</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Specialization</Label>
                      <Input
                        value={jobSeekerForm.specialization}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, specialization: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Computer Science"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">University/Institute</Label>
                      <Input
                        value={jobSeekerForm.university}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, university: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="University name"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Graduation Year</Label>
                      <Input
                        value={jobSeekerForm.graduationYear}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, graduationYear: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="2020"
                      />
                    </div>
                  </div>
                </div>

                {/* Previous Employment Section */}
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-blue-600 font-medium mb-3">Previous Employment (Optional)</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Previous Company</Label>
                      <Input
                        value={jobSeekerForm.previousCompany}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, previousCompany: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Previous company"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Designation</Label>
                      <Input
                        value={jobSeekerForm.previousDesignation}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, previousDesignation: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Role"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Duration</Label>
                      <Input
                        value={jobSeekerForm.previousDuration}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, previousDuration: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="2 years"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Details Section */}
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-blue-600 font-medium mb-3">Additional Details (Optional)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700 font-medium">Industry</Label>
                      <Input
                        value={jobSeekerForm.industry}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, industry: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Information Technology"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Functional Area</Label>
                      <Input
                        value={jobSeekerForm.functionalArea}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, functionalArea: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Software Development"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">LinkedIn URL</Label>
                      <Input
                        type="url"
                        value={jobSeekerForm.linkedinUrl}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, linkedinUrl: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="https://linkedin.com/in/username"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700 font-medium">Portfolio URL</Label>
                      <Input
                        type="url"
                        value={jobSeekerForm.portfolioUrl}
                        onChange={(e) => setJobSeekerForm({...jobSeekerForm, portfolioUrl: e.target.value})}
                        className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="https://portfolio.com"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  data-testid="submit-seeker-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Add Job Seeker
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs - Mobile responsive */}
        <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto bg-white rounded-t-lg px-4 md:hidden">
          <button
            onClick={() => setActiveTab('myJobs')}
            className={`pb-3 pt-4 px-1 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'myJobs'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            My Jobs ({jobs.length})
          </button>
          <button
            onClick={() => setActiveTab('allJobs')}
            className={`pb-3 pt-4 px-1 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'allJobs'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            All Live Jobs ({allLiveJobs.length})
          </button>
          <button
            onClick={() => setActiveTab('myJobSeekers')}
            className={`pb-3 pt-4 px-1 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'myJobSeekers'
                ? 'text-indigo-600 border-b-2 border-indigo-600'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            My Job Seekers ({myJobSeekers.length})
          </button>
          <button
            onClick={() => { setActiveTab('allJobSeekers'); setCurrentPage(1); }}
            className={`pb-3 pt-4 px-1 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'allJobSeekers'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            All Job Seekers ({allJobSeekers.length})
          </button>
        </div>

        {/* My Jobs Tab Content */}
        {activeTab === 'myJobs' && (
          <div className="grid gap-4">
          {jobs.length === 0 ? (
            <Card className="bg-white border-gray-200 p-8 text-center shadow-sm">
              <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-600">No jobs posted yet. Create your first job posting!</p>
            </Card>
          ) : (
            jobs.map(job => (
              <Card key={job.id} className="bg-white border-gray-200 p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{job.title}</h3>
                    <p className="text-blue-600 font-medium">{job.company}</p>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {job.experience}
                      </span>
                      <span className="flex items-center gap-1">
                        <IndianRupee className="w-4 h-4" /> {job.package}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" /> {job.applicants || 0} applicants
                      </span>
                    </div>
                    {job.keySkills && job.keySkills.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {job.keySkills.slice(0, 5).map((skill, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                            {skill}
                          </span>
                        ))}
                        {job.keySkills.length > 5 && (
                          <span className="px-2 py-1 text-gray-600 text-xs">+{job.keySkills.length - 5} more</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-gray-600 hover:text-blue-600"
                      onClick={() => handleEditJob(job)}
                      data-testid={`edit-job-${job.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-gray-600 hover:text-red-400"
                      onClick={() => handleDeleteJob(job.id)}
                      data-testid={`delete-job-${job.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
        )}

        {/* All Live Jobs Tab Content */}
        {activeTab === 'allJobs' && (
          <div className="grid gap-4">
          {allLiveJobs.length === 0 ? (
            <Card className="bg-white border-gray-200 p-8 text-center">
              <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-600">No live job postings available.</p>
            </Card>
          ) : (
            (Array.isArray(allLiveJobs) ? allLiveJobs : []).map(job => (
              <Card key={job.id} className="bg-white border-gray-200 p-5 hover:border-green-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Live</span>
                    </div>
                    <p className="text-blue-600 font-medium">{job.company}</p>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {job.experience}
                      </span>
                      <span className="flex items-center gap-1">
                        <IndianRupee className="w-4 h-4" /> {job.package}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" /> {job.numberOfOpenings} Opening{job.numberOfOpenings !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {job.keySkills && job.keySkills.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {job.keySkills.slice(0, 5).map((skill, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-4 flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs ${job.applicants > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                      {job.applicants} Applicant{job.applicants !== 1 ? 's' : ''}
                    </span>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-gray-900"
                      onClick={() => handleOpenAddToPipeline(job)}
                      data-testid={`add-candidate-${job.id}`}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Add Candidate
                    </Button>
                    <p className="text-gray-600 text-xs">
                      Posted: {job.createdAt ? new Date(job.createdAt).toLocaleDateString() : 'N/A'}
                    </p>
                    <p className="text-gray-600 text-xs">
                      By: {job.postedBy}
                    </p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
        )}

        {/* My Job Seekers Tab Content */}
        {/* My Job Seekers Tab Content */}
        {activeTab === 'myJobSeekers' && (
          <div className="grid gap-4">
            {sortedMyJobSeekers.length === 0 ? (
              <Card className="bg-white border-gray-200 p-8 text-center">
                <UserPlus className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No job seekers added yet.</p>
                <Button 
                  onClick={() => setShowJobSeekerModal(true)}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Your First Job Seeker
                </Button>
              </Card>
            ) : (
              sortedMyJobSeekers.map(seeker => (
                <Card key={seeker.id} className="bg-white border-gray-200 p-5 hover:border-indigo-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-gray-900 font-bold text-lg">
                        {seeker.fullName?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-gray-900">{seeker.fullName}</h3>
                          {isNewCandidate(seeker.createdAt) && (
                            <span className="px-2 py-0.5 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 text-xs rounded-full flex items-center gap-1 animate-pulse">
                              <Sparkles className="w-3 h-3" />
                              New
                            </span>
                          )}
                          {seeker.hasProfile && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Profile Complete</span>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm">{seeker.email}</p>
                        {seeker.headline && (
                          <p className="text-blue-600 text-sm mt-1">{seeker.headline}</p>
                        )}
                        
                        {/* Current Employment */}
                        {(seeker.currentDesignation || seeker.currentEmployer) && (
                          <div className="mt-2 bg-gray-100 rounded px-2 py-1.5">
                            <p className="text-gray-900 text-sm font-medium">
                              {seeker.currentDesignation || 'Employee'}
                              {seeker.currentEmployer && (
                                <span className="text-gray-600 font-normal"> at </span>
                              )}
                              {seeker.currentEmployer && (
                                <span className="text-blue-600">{seeker.currentEmployer}</span>
                              )}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                          {seeker.experience && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {seeker.experience}
                            </span>
                          )}
                          {seeker.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {seeker.location}
                            </span>
                          )}
                          {seeker.mobile && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {seeker.mobile}
                            </span>
                          )}
                        </div>
                        {seeker.skills && seeker.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {seeker.skills.map((skill, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-xs ${seeker.applicationCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                        {seeker.applicationCount} Application{seeker.applicationCount !== 1 ? 's' : ''}
                      </span>
                      <p className="text-gray-600 text-xs mt-2">
                        Added: {seeker.createdAt ? new Date(seeker.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* All Job Seekers Tab Content */}
        {/* All Job Seekers Tab Content */}
        {activeTab === 'allJobSeekers' && (
          <div className="flex gap-6">
            {/* Filters Sidebar */}
            <div className="w-72 flex-shrink-0">
              <CandidateFilters
                filters={candidateFilters}
                onFilterChange={setCandidateFilters}
                onClearFilters={handleClearCandidateFilters}
                searchQuery={candidateSearchQuery}
                onSearchQueryChange={setCandidateSearchQuery}
                totalCandidates={allJobSeekers.length}
              />
            </div>
            
            {/* Candidates List */}
            <div className="flex-1">
              {/* Header with selection, count, sort, and export */}
              <div className="flex items-center justify-between mb-4 bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-4">
                  {/* Select all checkbox */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="select-all"
                      checked={isAllOnPageSelected}
                      onCheckedChange={handleSelectAllOnPage}
                      className="border-gray-300"
                      data-testid="select-all-checkbox"
                    />
                    <label htmlFor="select-all" className="text-sm text-gray-600 cursor-pointer">
                      Select all
                    </label>
                  </div>
                  
                  {/* Selected count */}
                  {selectedCandidates.length > 0 && (
                    <span className="text-sm bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                      {selectedCandidates.length} selected
                    </span>
                  )}
                  
                  {/* Count and page info */}
                  <p className="text-gray-600 text-sm">
                    {sortedAllJobSeekers.length} candidate{sortedAllJobSeekers.length !== 1 ? 's' : ''} found
                    {totalPages > 1 && (
                      <span className="text-gray-500 ml-1">(Page {currentPage} of {totalPages})</span>
                    )}
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Download limit indicator */}
                  <div className="flex items-center gap-2 text-sm" data-testid="download-limit-indicator">
                    <span className={`px-2 py-1 rounded-full ${downloadLimit.remaining <= 10 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                      {downloadLimit.remaining}/{downloadLimit.dailyLimit} downloads left today
                    </span>
                  </div>
                  
                  {/* Export dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`border-green-500 text-green-600 hover:bg-green-50 ${(selectedCandidates.length === 0 || exportingExcel) ? 'opacity-50' : ''}`}
                        disabled={selectedCandidates.length === 0 || exportingExcel}
                        data-testid="export-dropdown-btn"
                      >
                        {exportingExcel ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        {exportingExcel ? 'Exporting...' : 'Export Excel'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white border border-gray-200">
                      <DropdownMenuItem 
                        onClick={() => exportToExcel('basic')}
                        className="cursor-pointer hover:bg-gray-100"
                        data-testid="export-basic-btn"
                      >
                        <FileText className="w-4 h-4 mr-2 text-gray-600" />
                        <div>
                          <p className="font-medium">Basic Info</p>
                          <p className="text-xs text-gray-500">Name, Company, Experience, CTC...</p>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => exportToExcel('contact')}
                        className="cursor-pointer hover:bg-gray-100"
                        data-testid="export-contact-btn"
                      >
                        <Phone className="w-4 h-4 mr-2 text-gray-600" />
                        <div>
                          <p className="font-medium">With Contact Info</p>
                          <p className="text-xs text-gray-500">Includes Mobile & Email</p>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* Sort dropdown */}
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-gray-600" />
                    <select
                      value={candidateSortBy}
                      onChange={(e) => { setCandidateSortBy(e.target.value); setCurrentPage(1); }}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="name">Name (A-Z)</option>
                  </select>
                  </div>
                </div>
              </div>
              
              {filteredJobSeekersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                  <span className="ml-3 text-gray-600">Filtering candidates...</span>
                </div>
              ) : sortedAllJobSeekers.length === 0 ? (
                <Card className="bg-white border-gray-200 p-8 text-center">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">No job seekers match your filters.</p>
                  <Button 
                    variant="outline" 
                    onClick={handleClearCandidateFilters}
                    className="border-purple-500 text-purple-600 hover:bg-purple-100"
                  >
                    Clear All Filters
                  </Button>
                </Card>
              ) : (
                <>
                <div className="grid gap-4">
                  {paginatedJobSeekers.map(seeker => (
                    <Card 
                      key={seeker.id} 
                      className={`bg-white border-gray-200 p-5 hover:border-blue-300 transition-colors ${selectedCandidates.includes(seeker.id) ? 'ring-2 ring-purple-400 border-purple-300' : ''}`} 
                      data-testid={`candidate-card-${seeker.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          {/* Selection checkbox */}
                          <div className="pt-1">
                            <Checkbox
                              id={`select-${seeker.id}`}
                              checked={selectedCandidates.includes(seeker.id)}
                              onCheckedChange={(checked) => handleSelectCandidate(seeker.id, checked)}
                              className="border-gray-300"
                              data-testid={`select-candidate-${seeker.id}`}
                            />
                          </div>
                          
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-gray-900 font-bold text-xl flex-shrink-0">
                            {seeker.fullName?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-lg font-semibold text-gray-900">{seeker.fullName}</h3>
                              {isNewCandidate(seeker.createdAt) && (
                                <span className="px-2 py-0.5 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 text-xs rounded-full flex items-center gap-1 animate-pulse">
                                  <Sparkles className="w-3 h-3" />
                                  New
                                </span>
                              )}
                              {seeker.hasProfile && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Profile</span>
                              )}
                              <span className={`px-2 py-0.5 rounded text-xs ${seeker.applicationCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                {seeker.applicationCount} App{seeker.applicationCount !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm">{seeker.email}</p>
                            {seeker.mobile && (
                              <p className="text-gray-600 text-sm flex items-center gap-1">
                                <Phone className="w-3.5 h-3.5" />
                                {seeker.mobile}
                              </p>
                            )}
                            {seeker.headline && (
                              <p className="text-purple-600 text-sm mt-1 line-clamp-1">{seeker.headline}</p>
                            )}
                            
                            {/* Current Employment */}
                            {(seeker.currentDesignation || seeker.currentEmployer) && (
                              <div className="mt-2 bg-gray-100 rounded px-2 py-1.5">
                                <p className="text-gray-900 text-sm font-medium">
                                  {seeker.currentDesignation || 'Employee'}
                                  {seeker.currentEmployer && (
                                    <span className="text-gray-600 font-normal"> at </span>
                                  )}
                                  {seeker.currentEmployer && (
                                    <span className="text-blue-600">{seeker.currentEmployer}</span>
                                  )}
                                </p>
                              </div>
                            )}
                            
                            {/* Key Info Row */}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                              {seeker.experience && (
                                <span className="flex items-center gap-1 text-gray-600">
                                  <Briefcase className="w-3.5 h-3.5" />
                                  {seeker.experience}
                                </span>
                              )}
                              {seeker.location && (
                                <span className="flex items-center gap-1 text-gray-600">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {seeker.location}
                                </span>
                              )}
                              {seeker.noticePeriod && (
                                <span className="flex items-center gap-1 text-yellow-500">
                                  <Clock className="w-3.5 h-3.5" />
                                  {seeker.noticePeriod}
                                </span>
                              )}
                            </div>
                            
                            {/* Salary Info */}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
                              {seeker.currentSalary && (
                                <span className="flex items-center gap-1 text-gray-600">
                                  <IndianRupee className="w-3.5 h-3.5" />
                                  Current: {seeker.currentSalary}
                                </span>
                              )}
                              {seeker.expectedSalary && (
                                <span className="flex items-center gap-1 text-green-600">
                                  <IndianRupee className="w-3.5 h-3.5" />
                                  Expected: {seeker.expectedSalary}
                                </span>
                              )}
                            </div>
                            
                            {/* Skills */}
                            {seeker.skills && seeker.skills.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {seeker.skills.map((skill, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                    {skill}
                                  </span>
                                ))}
                                {seeker.skills.length === 5 && (
                                  <span className="px-2 py-0.5 text-gray-600 text-xs">+more</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex flex-col items-end gap-2 ml-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-purple-500 text-purple-600 hover:bg-purple-100 h-8"
                              onClick={() => handleViewCandidate(seeker)}
                              data-testid={`view-candidate-${seeker.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-blue-500 text-blue-600 hover:bg-blue-100 h-8"
                              onClick={() => handleEditCandidate(seeker)}
                              data-testid={`edit-candidate-${seeker.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                          <p className="text-gray-600 text-xs">
                            Joined: {seeker.createdAt ? new Date(seeker.createdAt).toLocaleDateString() : 'N/A'}
                          </p>
                          {seeker.lastLogin && (
                            <p className="text-gray-600 text-xs">
                              Active: {new Date(seeker.lastLogin).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6 pb-4" data-testid="pagination-controls">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        // Show first page, last page, current page, and neighbors
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              className={currentPage === pageNum 
                                ? "bg-purple-600 hover:bg-purple-700 text-white min-w-[36px]" 
                                : "border-gray-300 text-gray-700 hover:bg-gray-100 min-w-[36px]"
                              }
                              data-testid={`page-${pageNum}-btn`}
                            >
                              {pageNum}
                            </Button>
                          );
                        } else if (
                          (pageNum === 2 && currentPage > 3) ||
                          (pageNum === totalPages - 1 && currentPage < totalPages - 2)
                        ) {
                          return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                      data-testid="next-page-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add to Pipeline Modal */}
      <Dialog open={showAddToPipelineModal} onOpenChange={(open) => {
        setShowAddToPipelineModal(open);
        if (!open) {
          setSelectedJobForPipeline(null);
          setPipelineSearchQuery('');
        }
      }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add Candidate to Pipeline
            </DialogTitle>
          </DialogHeader>
          
          {selectedJobForPipeline && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600">Adding candidate to:</p>
              <p className="text-lg font-semibold text-gray-900">{selectedJobForPipeline.title}</p>
              <p className="text-blue-600">{selectedJobForPipeline.company}</p>
            </div>
          )}
          
          {/* Search Input */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or skills..."
                value={pipelineSearchQuery}
                onChange={(e) => setPipelineSearchQuery(e.target.value)}
                className="pl-10 bg-gray-50 border-gray-300 text-gray-900 focus:border-green-500 focus:ring-green-500"
                data-testid="pipeline-search-input"
              />
              {pipelineSearchQuery && (
                <button
                  onClick={() => setPipelineSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {filteredPipelineCandidates.length} candidate{filteredPipelineCandidates.length !== 1 ? 's' : ''} found
            </p>
          </div>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {filteredPipelineCandidates.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {pipelineSearchQuery ? 'No candidates match your search' : 'No job seekers available'}
                </p>
                {pipelineSearchQuery && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 border-gray-300 text-gray-600"
                    onClick={() => setPipelineSearchQuery('')}
                  >
                    Clear Search
                  </Button>
                )}
              </div>
            ) : (
              filteredPipelineCandidates.map(seeker => (
                <div 
                  key={seeker.id} 
                  className="flex items-center justify-between p-3 bg-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-gray-900 font-bold">
                      {seeker.fullName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{seeker.fullName}</p>
                        {seeker.hasProfile && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">Profile</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{seeker.email}</p>
                      {seeker.headline && (
                        <p className="text-xs text-purple-600 mt-0.5">{seeker.headline}</p>
                      )}
                      <div className="flex gap-2 mt-1 text-xs text-gray-600">
                        {seeker.experience && <span>{seeker.experience}</span>}
                        {seeker.location && <span>• {seeker.location}</span>}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleAddToPipeline(seeker.id)}
                    disabled={submitting}
                    data-testid={`add-seeker-to-pipeline-${seeker.id}`}
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Candidate Modal */}
      <Dialog open={showViewCandidateModal} onOpenChange={(open) => {
        setShowViewCandidateModal(open);
        if (!open) {
          setSelectedCandidate(null);
          setCandidateDetails(null);
          setShowPipelineSelector(false);
          setSelectedJobForCandidate('');
        }
      }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5" />
              Candidate Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingCandidateDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : candidateDetails ? (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-gray-900 font-bold text-2xl flex-shrink-0">
                  {candidateDetails.fullName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">{candidateDetails.fullName}</h2>
                  <p className="text-gray-600">{candidateDetails.email}</p>
                  {candidateDetails.profile?.headline && (
                    <p className="text-purple-600 mt-1">{candidateDetails.profile.headline}</p>
                  )}
                  {candidateDetails.mobile && (
                    <p className="text-gray-600 text-sm mt-1">📞 {candidateDetails.mobile}</p>
                  )}
                </div>
                <div className="text-right">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    {candidateDetails.totalApplications} Application{candidateDetails.totalApplications !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Professional Info */}
              {candidateDetails.profile && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-100 rounded-lg p-4">
                    <h3 className="text-blue-600 font-medium mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Professional Info
                    </h3>
                    <div className="space-y-2 text-sm">
                      {candidateDetails.profile.experience && (
                        <p><span className="text-gray-600">Experience:</span> <span className="text-gray-900">{candidateDetails.profile.experience}</span></p>
                      )}
                      {candidateDetails.profile.currentRole && (
                        <p><span className="text-gray-600">Current Role:</span> <span className="text-gray-900">{candidateDetails.profile.currentRole}</span></p>
                      )}
                      {candidateDetails.profile.currentCompany && (
                        <p><span className="text-gray-600">Current Company:</span> <span className="text-blue-600 font-medium">{candidateDetails.profile.currentCompany}</span></p>
                      )}
                      {candidateDetails.profile.industry && (
                        <p><span className="text-gray-600">Industry:</span> <span className="text-gray-900">{candidateDetails.profile.industry}</span></p>
                      )}
                      {candidateDetails.profile.location && (
                        <p><span className="text-gray-600">Location:</span> <span className="text-gray-900">{candidateDetails.profile.location}</span></p>
                      )}
                      {candidateDetails.profile.noticePeriod && (
                        <p><span className="text-gray-600">Notice Period:</span> <span className="text-yellow-600">{candidateDetails.profile.noticePeriod}</span></p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-gray-100 rounded-lg p-4">
                    <h3 className="text-green-600 font-medium mb-3 flex items-center gap-2">
                      <IndianRupee className="w-4 h-4" />
                      Salary Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      {candidateDetails.profile.currentSalary && (
                        <p><span className="text-gray-600">Current:</span> <span className="text-gray-900">{candidateDetails.profile.currentSalary}</span></p>
                      )}
                      {candidateDetails.profile.expectedSalary && (
                        <p><span className="text-gray-600">Expected:</span> <span className="text-green-600">{candidateDetails.profile.expectedSalary}</span></p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Skills */}
              {candidateDetails.profile?.skills && candidateDetails.profile.skills.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-purple-600 font-medium mb-3">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(candidateDetails?.profile?.skills) ? candidateDetails.profile.skills : []).map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Work Experience */}
              {candidateDetails.profile?.workExperience && candidateDetails.profile.workExperience.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-blue-600 font-medium mb-3">Work Experience</h3>
                  <div className="space-y-3">
                    {candidateDetails.profile.workExperience.map((exp, idx) => (
                      <div key={idx} className="border-l-2 border-blue-500 pl-3">
                        <p className="text-gray-900 font-medium">{exp.role || exp.designation}</p>
                        <p className="text-gray-600 text-sm">{exp.company}</p>
                        {exp.duration && <p className="text-gray-600 text-xs">{exp.duration}</p>}
                        {exp.current && <span className="text-green-600 text-xs">Current</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {candidateDetails.profile?.education && candidateDetails.profile.education.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-yellow-600 font-medium mb-3">Education</h3>
                  <div className="space-y-3">
                    {candidateDetails.profile.education.map((edu, idx) => (
                      <div key={idx} className="border-l-2 border-yellow-500 pl-3">
                        <p className="text-gray-900 font-medium">{edu.degree}</p>
                        <p className="text-gray-600 text-sm">{edu.institution}</p>
                        {edu.fieldOfStudy && <p className="text-gray-600 text-sm">{edu.fieldOfStudy}</p>}
                        {edu.endYear && <p className="text-gray-600 text-xs">{edu.endYear}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Applications */}
              {candidateDetails.applications && candidateDetails.applications.length > 0 && (
                <div className="bg-gray-100 rounded-lg p-4">
                  <h3 className="text-orange-600 font-medium mb-3">Application History</h3>
                  <div className="space-y-2">
                    {(Array.isArray(candidateDetails?.applications) ? candidateDetails.applications : []).map((app, idx) => (
                      <div key={idx} className="bg-white border border-gray-200 rounded p-3 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-gray-900 text-sm font-medium">{app.jobTitle}</p>
                            <p className="text-gray-500 text-xs">{app.company}</p>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              app.currentStage === 'hired' ? 'bg-green-100 text-green-700' :
                              app.currentStage === 'rejected' ? 'bg-red-500/20 text-red-400' :
                              app.currentStage === 'offer' ? 'bg-purple-500/20 text-purple-600' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {app.currentStage}
                            </span>
                            <p className="text-gray-600 text-xs mt-1">
                              {app.appliedAt ? new Date(app.appliedAt).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </div>
                        {/* Withdraw/Reassign buttons for recruiter-assigned applications */}
                        {/* HR Assignment Controls */}
                        <div className="mt-2 pt-2 border-t border-gray-100 flex justify-end gap-2">

                        {/* If NOT assigned to HR yet */}
                        {!app.assignedHRId && (
                        <button
                        onClick={() => handleAssignToHR(app.id, candidateDetails.fullName)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
    >
                        <UserPlus className="w-3 h-3" />
                        Assign to HR
                        </button>
                        )}

                        {/* If assigned and NOT withdrawn */}
                        {app.assignedHRId && !app.isWithdrawn && (
                        <button
                        onClick={() => handleWithdrawFromHR(app.id, candidateDetails.fullName)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-orange-600 hover:bg-orange-50 rounded border border-orange-200"
    >
                        <UserMinus className="w-3 h-3" />
                        Withdraw from HR
                        </button>
                        )}

                    {/* If withdrawn */}
                      {app.assignedHRId && app.isWithdrawn && (
                      <button
                      onClick={() => handleReassignToHR(app.id, candidateDetails.fullName)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded border border-green-200"
    > 
                    <UserPlus className="w-3 h-3" />
                    Re-assign to HR
                    </button>
                        )}

                        </div>
                        {app.isWithdrawn && (
                          <p className="text-xs text-orange-500 mt-1">⚠️ Withdrawn from HR</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Links */}
              {(candidateDetails.profile?.linkedinUrl || candidateDetails.profile?.portfolioUrl || candidateDetails.profile?.resumeUrl) && (
                <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                  {candidateDetails.profile.linkedinUrl && (
                    <a href={candidateDetails.profile.linkedinUrl} target="_blank" rel="noopener noreferrer" 
                       className="px-3 py-1.5 bg-blue-600/20 text-blue-600 rounded hover:bg-blue-600/30 text-sm">
                      LinkedIn Profile
                    </a>
                  )}
                  {candidateDetails.profile.portfolioUrl && (
                    <a href={candidateDetails.profile.portfolioUrl} target="_blank" rel="noopener noreferrer"
                       className="px-3 py-1.5 bg-purple-600/20 text-purple-600 rounded hover:bg-purple-600/30 text-sm">
                      Portfolio
                    </a>
                  )}
                  {candidateDetails.profile.resumeUrl && (
                    <a href={candidateDetails.profile.resumeUrl} target="_blank" rel="noopener noreferrer"
                       className="px-3 py-1.5 bg-green-600/20 text-green-600 rounded hover:bg-green-600/30 text-sm">
                      Download Resume
                    </a>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-4 border-t border-gray-200 space-y-4">
                {/* Add to Pipeline Section */}
                {!showPipelineSelector ? (
                  <Button
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    onClick={() => setShowPipelineSelector(true)}
                    data-testid="add-to-pipeline-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Job Pipeline
                  </Button>
                ) : (
                  <div className="bg-gray-100 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-green-600 font-medium">Select Job</h4>
                      <button 
                        onClick={() => {
                          setShowPipelineSelector(false);
                          setSelectedJobForCandidate('');
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        ✕
                      </button>
                    </div>
                    <select
                      value={selectedJobForCandidate}
                      onChange={(e) => setSelectedJobForCandidate(e.target.value)}
                      className="w-full h-10 px-3 bg-gray-50 border border-gray-300 text-gray-900 rounded-md focus:border-blue-500 focus:ring-blue-500"
                      data-testid="pipeline-job-select"
                    >
                      <option value="">-- Select a job --</option>
                      {(Array.isArray(allLiveJobs) ? allLiveJobs : []).map(job => (
                        <option key={job.id} value={job.id}>
                          {job.title} - {job.company}
                        </option>
                      ))}
                    </select>
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={handleAddCandidateToPipeline}
                      disabled={!selectedJobForCandidate || addingToPipeline}
                      data-testid="confirm-add-to-pipeline"
                    >
                      {addingToPipeline ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <UserPlus className="w-4 h-4 mr-2" />
                      )}
                      Add to Pipeline
                    </Button>
                  </div>
                )}
                
                {/* Other Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 flex-1"
                    onClick={() => {
                      setShowViewCandidateModal(false);
                      setShowPipelineSelector(false);
                      handleEditCandidate(selectedCandidate);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                  <Button
                    variant="outline"
                    className="border-gray-300 text-gray-300 hover:bg-gray-50"
                    onClick={() => {
                      setShowViewCandidateModal(false);
                      setShowPipelineSelector(false);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">No details available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Candidate Modal */}
      <Dialog open={showEditCandidateModal} onOpenChange={(open) => {
        setShowEditCandidateModal(open);
        if (!open) setSelectedCandidate(null);
      }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Candidate Profile
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveCandidate} className="space-y-4">
            {/* Basic Info */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="text-blue-600 font-medium mb-3">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700 font-medium">Full Name *</Label>
                  <Input
                    required
                    value={editCandidateForm.fullName}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, fullName: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Phone</Label>
                  <Input
                    value={editCandidateForm.phone}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, phone: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-700 font-medium">Location</Label>
                  <Input
                    value={editCandidateForm.location}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, location: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="City, State"
                  />
                </div>
              </div>
            </div>

            {/* Professional Summary */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="text-purple-600 font-medium mb-3">Professional Summary</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-700 font-medium">Resume Headline</Label>
                  <Input
                    value={editCandidateForm.resumeHeadline}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, resumeHeadline: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Senior Developer | 5+ Years Experience"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Experience</Label>
                    <select
                      value={editCandidateForm.experience}
                      onChange={(e) => setEditCandidateForm({...editCandidateForm, experience: e.target.value})}
                      className="w-full h-10 px-3 bg-gray-50 border border-gray-300 text-gray-900 rounded-md focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select</option>
                      <option value="Fresher">Fresher</option>
                      <option value="0-1 years">0-1 years</option>
                      <option value="1-2 years">1-2 years</option>
                      <option value="2-3 years">2-3 years</option>
                      <option value="3-5 years">3-5 years</option>
                      <option value="5-7 years">5-7 years</option>
                      <option value="7-10 years">7-10 years</option>
                      <option value="10-15 years">10-15 years</option>
                      <option value="15+ years">15+ years</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Notice Period</Label>
                    <select
                      value={editCandidateForm.noticePeriod}
                      onChange={(e) => setEditCandidateForm({...editCandidateForm, noticePeriod: e.target.value})}
                      className="w-full h-10 px-3 bg-gray-50 border border-gray-300 text-gray-900 rounded-md focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select</option>
                      <option value="Immediate">Immediate</option>
                      <option value="15 days">15 days</option>
                      <option value="30 days">30 days</option>
                      <option value="45 days">45 days</option>
                      <option value="60 days">60 days</option>
                      <option value="90 days">90 days</option>
                      <option value="More than 90 days">More than 90 days</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700 font-medium">Current Role</Label>
                    <Input
                      value={editCandidateForm.currentRole}
                      onChange={(e) => setEditCandidateForm({...editCandidateForm, currentRole: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Software Engineer"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700 font-medium">Current Company</Label>
                    <Input
                      value={editCandidateForm.currentCompany}
                      onChange={(e) => setEditCandidateForm({...editCandidateForm, currentCompany: e.target.value})}
                      className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="TechCorp India"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Industry</Label>
                  <Input
                    value={editCandidateForm.industry}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, industry: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Information Technology"
                  />
                </div>
              </div>
            </div>

            {/* Salary */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="text-green-600 font-medium mb-3">Salary Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700 font-medium">Current Salary</Label>
                  <Input
                    value={editCandidateForm.currentPackage}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, currentPackage: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="12 LPA"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Expected Salary</Label>
                  <Input
                    value={editCandidateForm.expectedPackage}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, expectedPackage: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="18-20 LPA"
                  />
                </div>
              </div>
            </div>

            {/* Skills */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="text-yellow-600 font-medium mb-3">Skills</h3>
              <div>
                <Label className="text-gray-700 font-medium">Skills (comma-separated)</Label>
                <Input
                  value={editCandidateForm.skills}
                  onChange={(e) => setEditCandidateForm({...editCandidateForm, skills: e.target.value})}
                  className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="React, Node.js, Python, AWS"
                />
              </div>
            </div>

            {/* Links */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="text-blue-600 font-medium mb-3">Links</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700 font-medium">LinkedIn URL</Label>
                  <Input
                    value={editCandidateForm.linkedinUrl}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, linkedinUrl: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 font-medium">Portfolio URL</Label>
                  <Input
                    value={editCandidateForm.portfolioUrl}
                    onChange={(e) => setEditCandidateForm({...editCandidateForm, portfolioUrl: e.target.value})}
                    className="bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="https://portfolio.com"
                  />
                </div>
              </div>
            </div>

            {/* File Uploads */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="text-orange-600 font-medium mb-3 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Documents & Photo
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Resume Upload */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Resume (PDF, DOC, DOCX)
                  </Label>
                  <div className="mt-2">
                    <label className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-600 border border-slate-500 border-dashed rounded-lg cursor-pointer hover:bg-slate-600/80 transition-colors">
                      <Upload className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-300 text-sm">
                        {candidateResumeFile ? candidateResumeFile.name : 'Choose Resume'}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleResumeFileChange}
                        className="hidden"
                      />
                    </label>
                    {candidateResumeFile && (
                      <p className="text-green-600 text-xs mt-1">✓ File selected: {candidateResumeFile.name}</p>
                    )}
                    {candidateDetails?.profile?.resumeUrl && !candidateResumeFile && (
                      <p className="text-blue-600 text-xs mt-1">Existing resume available</p>
                    )}
                  </div>
                </div>
                
                {/* Photo Upload */}
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Profile Photo (JPG, PNG)
                  </Label>
                  <div className="mt-2">
                    <label className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-600 border border-slate-500 border-dashed rounded-lg cursor-pointer hover:bg-slate-600/80 transition-colors">
                      <Camera className="w-5 h-5 text-gray-600" />
                      <span className="text-gray-300 text-sm">
                        {candidatePhotoFile ? candidatePhotoFile.name : 'Choose Photo'}
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePhotoFileChange}
                        className="hidden"
                      />
                    </label>
                    {candidatePhotoFile && (
                      <p className="text-green-600 text-xs mt-1">✓ File selected: {candidatePhotoFile.name}</p>
                    )}
                    {candidateDetails?.profile?.photoUrl && !candidatePhotoFile && (
                      <p className="text-blue-600 text-xs mt-1">Existing photo available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                type="submit" 
                disabled={submitting || uploadingResume || uploadingPhoto}
                className="bg-green-600 hover:bg-green-700 flex-1"
              >
                {(submitting || uploadingResume || uploadingPhoto) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {uploadingResume ? 'Uploading Resume...' : uploadingPhoto ? 'Uploading Photo...' : 'Save Changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="border-gray-300 text-gray-300 hover:bg-gray-50"
                onClick={() => setShowEditCandidateModal(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecruiterDashboard;
